/**
 * Figure S4's second roster chip — "🔗 Shared in · … · …" (review round 2,
 * item 4).
 *
 * WHAT THE REAL DATA SAYS. Checked against bob's live account on 6 Sep 2026:
 *   - GET /whitespace/portfolio?scope=own returns `shared_in_sources: []`, so
 *     there is no disclosure-ladder source shared IN to him.
 *   - GET /aggregator-grants/projects/{id}/grants DOES return real shares: four
 *     of his nine inventions are shared into one named portfolio ("Acme
 *     Aggregator"), granted by bob himself, scope project_subtree, no expiry.
 *
 * So the chip is drawn from the grants — the real relationship this account
 * actually has — naming the portfolio, and saying how many of the caller's own
 * inventions are in it. One chip per portfolio, not one per invention, because
 * the reader's question is "who can see my work", not "which row is which".
 *
 * When a portfolio's grant carries an expiry or folder exclusions, the chip says
 * so: an expiring or partial share is not the same promise as an open one, and
 * a reader who is not told cannot tell them apart.
 */
import { STAGES } from '../graph/DisclosureLevel'
import type { AggregatorGrant, DisclosureAgreement } from '../../types'

export interface SharedInPortfolio {
  aggregator_id: string
  /** The portfolio's real name; absent when the route did not carry one. */
  name?: string
  /** How many of the caller's own inventions are shared into it. */
  inventionCount: number
  /** True when at least one of those grants hides folders from the portfolio. */
  hasExclusions: boolean
  /** The soonest expiry across those grants, when any is set. */
  expiresAt?: number
  /** The disclosure rung the grant records, when it records one at all. */
  disclosureLevel?: number
  /** True when at least one grant opens the caller's wider set to the portfolio. */
  extendedVisibility?: boolean
}

/**
 * THE SECOND PLACE A RUNG CAN LIVE — the disclosure agreement between the
 * caller's org and the portfolio's, whose `current_level` is the very number
 * the backend enforces (disclosure.filters.get_disclosure_level reads
 * `current_level` and nothing else). A grant may carry no rung while an
 * agreement with the same counterparty does; when it does, that rung is what
 * the portfolio really sees, so the chip names it.
 *
 * Measured on bob's live account, 6 Sep 2026: his one agreement is with org
 * b9bea4e8… at level 1 — NOT with the portfolio he shared into (895e4448…,
 * "Acme Aggregator"), which reaches his work through a project_subtree grant
 * instead. So on this account the map is empty for that portfolio, and the
 * chip falls back to describing what the grant actually gives. The lookup is
 * still wired because a rung is the better answer wherever one exists.
 */
export function consentedLevelsByOrg(
  agreements: DisclosureAgreement[] | undefined,
): Map<string, number> {
  const byOrg = new Map<string, number>()
  for (const a of agreements ?? []) {
    const mine = a.my_org_id
    const other =
      a.counterparty_org_id ?? (mine === a.org_a_id ? a.org_b_id : mine === a.org_b_id ? a.org_a_id : undefined)
    const level = typeof a.current_level === 'number' ? a.current_level : undefined
    if (!other || level === undefined) continue
    // Two agreements with one counterparty: the narrowest is the honest one.
    const seen = byOrg.get(other)
    byOrg.set(other, seen === undefined ? level : Math.min(seen, level))
  }
  return byOrg
}

/** Roll every owned project's grants up by portfolio. Input is the real
 * per-project grant list; a project with no grant contributes nothing.
 * `consentedLevels` (from consentedLevelsByOrg) supplies a rung for a
 * portfolio whose grants do not carry one. */
export function rollUpSharedInPortfolios(
  perProject: { projectId: string; grants: AggregatorGrant[] }[],
  consentedLevels?: Map<string, number>,
): SharedInPortfolio[] {
  const byPortfolio = new Map<string, SharedInPortfolio>()
  for (const { grants } of perProject) {
    for (const grant of grants ?? []) {
      const existing = byPortfolio.get(grant.aggregator_id)
      const expiry = typeof grant.expires_at === 'number' ? grant.expires_at : undefined
      const excluded = (grant.exclusions ?? []).length > 0
      const rung = typeof grant.disclosure_level === 'number' ? grant.disclosure_level : undefined
      if (existing) {
        existing.inventionCount += 1
        existing.hasExclusions = existing.hasExclusions || excluded
        existing.name = existing.name ?? grant.aggregator_name
        existing.extendedVisibility = existing.extendedVisibility || grant.extended_visibility === true
        // The narrowest rung across the grants — what the portfolio sees of the
        // LEAST-open of them is the honest headline for the whole relationship.
        if (rung !== undefined) {
          existing.disclosureLevel =
            existing.disclosureLevel === undefined ? rung : Math.min(existing.disclosureLevel, rung)
        }
        if (expiry !== undefined) {
          existing.expiresAt =
            existing.expiresAt === undefined ? expiry : Math.min(existing.expiresAt, expiry)
        }
      } else {
        byPortfolio.set(grant.aggregator_id, {
          aggregator_id: grant.aggregator_id,
          name: grant.aggregator_name,
          inventionCount: 1,
          hasExclusions: excluded,
          expiresAt: expiry,
          disclosureLevel: rung,
          extendedVisibility: grant.extended_visibility === true,
        })
      }
    }
  }
  // A portfolio whose grants recorded no rung still gets one if an agreement
  // with that org does — the grant is only the FIRST place to look.
  for (const p of byPortfolio.values()) {
    if (p.disclosureLevel === undefined) {
      const consented = consentedLevels?.get(p.aggregator_id)
      if (consented !== undefined && consented > 0) p.disclosureLevel = consented
    }
  }
  return [...byPortfolio.values()].sort((a, b) => b.inventionCount - a.inventionCount)
}

/**
 * WHAT THE PORTFOLIO ACTUALLY SEES — the chip's third segment (review round 3,
 * item 6). The figure's chip names a disclosure profile ("Categorical Profile"),
 * so this names the rung when the relationship HAS one.
 *
 * Two places can carry one, and both are read: the grant itself, then the
 * disclosure agreement with that org (consentedLevelsByOrg above). On bob's
 * live account NEITHER does for the portfolio he shares into — a grant carries
 * `scope`, `exclusions` and `expires_at` and no rung, because it is a direct
 * read of the project subtree rather than a step on the disclosure ladder, and
 * his only agreement is with a different org. Printing "Categorical Profile"
 * there would be naming a rung the relationship does not have. So a grant with
 * no rung is described by what it really gives, and a rung is printed whenever
 * one is actually served.
 */
export function sharedInProfileName(p: SharedInPortfolio): string {
  if (p.disclosureLevel !== undefined) {
    const stage = STAGES.find((s) => s.level === p.disclosureLevel)
    if (stage) return stage.name
  }
  if (p.hasExclusions) return 'Full project access, minus the folders you hid'
  if (p.extendedVisibility) return 'Full project access, plus your extended visibility'
  return 'Full project access'
}

/** The chip's own sentence: who sees it, and what they see. Never an id — a
 * portfolio with no served name is called what it is, "a portfolio". The COUNT
 * lives in the tooltip, because the figure's third segment is the profile. */
export function sharedInChipText(p: SharedInPortfolio): string {
  const who = p.name ?? 'a portfolio'
  const parts = [`🔗 Shared in · ${who} · ${sharedInProfileName(p)}`]
  if (p.expiresAt !== undefined) {
    parts.push(`until ${new Date(p.expiresAt * 1000).toLocaleDateString('en-AU')}`)
  }
  return parts.join(' · ')
}

/** The chip's hover text — the count the third segment used to carry, plus the
 * one thing a reader needs to know about a share. */
export function sharedInChipTooltip(p: SharedInPortfolio): string {
  const n = p.inventionCount
  return `${n} of your invention${n === 1 ? '' : 's'} shared into this portfolio — sharing is yours to give and yours to end`
}
